import { useCallback, useEffect, useState } from "react";
import { GetMasterHistoryConfig, GetMasterHistoryEntities } from "../../Service/_index";

const ALLOWED_TYPES = ["client", "accountant"];

// Login can store an array of roles, which sessionStorage joins with commas.
export const canViewMasterHistory = () =>
  String(sessionStorage.getItem("userType") || "")
    .split(",")
    .some((type) => ALLOWED_TYPES.includes(type.trim().toLowerCase()));

const clientKey = () => sessionStorage.getItem("clientID") || "";

// Every History icon in a table asks for this; one request per client serves
// them all. Failures are never stored, so a later mount tries again.
const resolved = new Map();
const inFlight = new Map();

// "Off" is remembered only briefly: long enough that paging through a table
// does not re-ask for every page, short enough that a flag switched on at the
// server is picked up soon.
const OFF_TTL_MS = 60 * 1000;
const knownOff = new Map();
const isKnownOff = (key) => Date.now() - (knownOff.get(key) || 0) < OFF_TTL_MS;
const OFF = { enabled: false, entities: [] };

const loadAccess = (key) => {
  if (resolved.has(key)) return Promise.resolve({ value: resolved.get(key) });
  if (isKnownOff(key)) return Promise.resolve({ value: OFF });
  if (!inFlight.has(key)) {
    const request = (async () => {
      const config = await GetMasterHistoryConfig();
      if (config.error) return { error: config.message };
      if (!config.enabled) {
        knownOff.set(key, Date.now());
        return { value: OFF };
      }
      const entities = await GetMasterHistoryEntities();
      if (entities.error) return { error: entities.message };
      const value = { enabled: true, entities: entities.data || [] };
      resolved.set(key, value);
      return { value };
    })().finally(() => inFlight.delete(key));
    inFlight.set(key, request);
  }
  return inFlight.get(key);
};

const NOT_READY = { ready: false, enabled: false, entities: [], error: null };

const initialState = (allowed, key) => {
  if (!allowed) return { ...NOT_READY, ready: true };
  if (resolved.has(key)) return { ...NOT_READY, ...resolved.get(key), ready: true };
  if (isKnownOff(key)) return { ...NOT_READY, ...OFF, ready: true };
  return NOT_READY;
};

/**
 * `{ allowed, ready, enabled, entities, error, retry }`. History UI should
 * render only when `allowed && enabled`; `error` is set when the status could
 * not be determined at all.
 */
export const useMasterHistoryAccess = () => {
  const allowed = canViewMasterHistory();
  const key = clientKey();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState(() => initialState(allowed, key));

  useEffect(() => {
    if (!allowed || state.ready) return undefined;
    let cancelled = false;
    loadAccess(key).then((result) => {
      if (cancelled) return;
      setState(
        result.error
          ? { ...NOT_READY, ready: true, error: result.error }
          : { ...NOT_READY, ...result.value, ready: true }
      );
    });
    return () => {
      cancelled = true;
    };
  }, [allowed, key, attempt, state.ready]);

  const retry = useCallback(() => {
    setState(NOT_READY);
    setAttempt((n) => n + 1);
  }, []);

  return { allowed, retry, ...state };
};
